/*
 * Consentimento de cookies (LGPD) + Google Consent Mode v2.
 *
 * Três categorias, como o cliente definiu no banner:
 * - Necessários: sessão, sacola, idioma. Sempre ativos, não passam por aqui.
 * - Analíticos: Google Analytics (analytics_storage).
 * - Marketing: Google Ads e Meta (ad_storage, ad_user_data, ad_personalization).
 *
 * O padrão "negado" é definido antes do GTM carregar, num script do index.html
 * (ver vite.config.ts) que também reaplica a escolha salva — assim quem já
 * decidiu não passa nem um instante sem a escolha valendo. Este arquivo cuida
 * do resto: ler, salvar, aplicar quando a pessoa clica e reabrir o banner.
 *
 * A chave, o formato e o prazo são os mesmos do script que o cliente mandou,
 * para uma escolha feita num continuar valendo no outro.
 */

export const CHAVE_CONSENTIMENTO = 'dms_lgpd_consent';
/** Validade da escolha. Depois disso o banner volta a aparecer. */
export const DIAS_CONSENTIMENTO = 180;
/** Evento que reabre o banner (link "Preferências de cookies" do rodapé). */
export const EVENTO_ABRIR_COOKIES = 'dms:abrir-cookies';

export interface Consentimento {
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

export function lerConsentimento(): Consentimento | null {
  try {
    const raw = localStorage.getItem(CHAVE_CONSENTIMENTO);
    if (!raw) return null;
    const c = JSON.parse(raw) as Partial<Consentimento>;
    if (typeof c.timestamp !== 'number') return null;
    if (Date.now() - c.timestamp > DIAS_CONSENTIMENTO * 24 * 60 * 60 * 1000) return null;
    return { analytics: Boolean(c.analytics), marketing: Boolean(c.marketing), timestamp: c.timestamp };
  } catch {
    return null;
  }
}

/** O visitante permitiu o Google Analytics. */
export function analyticsPermitido(): boolean {
  return lerConsentimento()?.analytics === true;
}

/**
 * O gtag do Consent Mode.
 *
 * Tem de empurrar o objeto `arguments`, e não um array: é assim que o GTM
 * reconhece um comando de consentimento no dataLayer.
 */
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/**
 * Salva e aplica a escolha.
 *
 * Passa direto pelo dataLayer, e não pelo módulo de rastreamento: a escolha de
 * cookies precisa chegar ao GTM para todo mundo, inclusive a equipe, cujos
 * eventos de funil são descartados.
 */
export function salvarConsentimento(analytics: boolean, marketing: boolean): Consentimento {
  const c: Consentimento = { analytics, marketing, timestamp: Date.now() };
  try {
    localStorage.setItem(CHAVE_CONSENTIMENTO, JSON.stringify(c));
  } catch {
    // Armazenamento bloqueado: a escolha vale nesta página e o banner volta na próxima.
  }
  try {
    gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
    });
    // Para tags que não entendem o Consent Mode (Meta Pixel): use estas
    // variáveis como condição no acionador.
    window.dataLayer!.push({
      event: 'dms_consent_update',
      dms_analytics_consent: analytics,
      dms_marketing_consent: marketing,
    });
  } catch {
    // rastreamento nunca interrompe o site
  }
  return c;
}

export function abrirPreferenciasDeCookies() {
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_COOKIES));
}
